/**
 * The customer a `/customers/:customerId/...` page is about (v1 completion C3).
 *
 * A customer's URL carries its CIS code (`CUS-…`); old links, emails and
 * bookmarks carry its Tharstern code. This reads the URL's key, resolves it
 * through the customer read (`getCustomerById` accepts either code), moves a
 * Tharstern-code URL onto the CIS code (replacing the history entry), and
 * returns the id every customer panel keys on: the Tharstern code.
 *
 * Returns `null` only while a CIS-code key is being resolved; a Tharstern-code
 * key is usable at once (it is already the id). A key no customer has is
 * returned as is, so the page shows its own not-found state.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";

const CIS_CUSTOMER_CODE = /^CUS-/;

type Resolution = { key: string; id: string; cisCode: string | null };

// Either code -> the customer's id, for the session, so the page moved onto the
// CIS code renders at once instead of waiting on a second read.
const resolvedIds = new Map<string, string>();

export function resetCustomerRouteIdCacheForTests(): void {
  resolvedIds.clear();
}

export function useCustomerRouteId(): string | null {
  const { customerId: key = "" } = useParams<{ customerId: string }>();
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const location = useLocation();
  const [resolution, setResolution] = useState<Resolution | null>(null);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    adapter
      .getCustomerById(key)
      .then((detail) => {
        if (cancelled) return;
        const id = detail?.customer?.id ?? key;
        const cisCode = detail?.customer?.cis_customer_code ?? null;
        resolvedIds.set(key, id);
        if (cisCode) resolvedIds.set(cisCode, id);
        setResolution({ key, id, cisCode });
      })
      .catch(() => {
        if (cancelled) return;
        resolvedIds.set(key, key);
        setResolution({ key, id: key, cisCode: null });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, key]);

  useEffect(() => {
    if (!resolution || resolution.key !== key) return;
    const { cisCode } = resolution;
    if (!cisCode || cisCode === key) return;
    const from = `/customers/${encodeURIComponent(key)}`;
    if (!location.pathname.startsWith(from)) return;
    const to = `/customers/${encodeURIComponent(cisCode)}`;
    navigate(
      `${to}${location.pathname.slice(from.length)}${location.search}${location.hash}`,
      {
        replace: true,
      },
    );
  }, [resolution, key, location, navigate]);

  const known = resolvedIds.get(key);
  if (known !== undefined) return known;
  return CIS_CUSTOMER_CODE.test(key) ? null : key;
}
